import { useEffect, useMemo, useRef } from 'react'

import { HOTEL_SEARCH_CHAT_PROMPT_QUERY_PARAM } from 'tripPlanner/config'
import {
  CLASSIC_TRIP_MIGRATION_QUERY_PARAM,
  consumeClassicTripMigrationPrompt,
} from 'tripPlanner/utils/classicTripMigrationPrompt'
import {
  STAGED_CHAT_PROMPT_QUERY_PARAM,
  consumeStagedChatPrompt,
} from 'tripPlanner/utils/stagedChatPrompt'

interface UsePromptTokenSubmissionArgs {
  search: string
  isNewChatRoute: boolean
  chatId: number | undefined
  isLoading: boolean
  isStreaming: boolean
  onPromptSubmit: (message: string) => Promise<void> | void
}

export function usePromptTokenSubmission({
  search,
  isNewChatRoute,
  chatId,
  isLoading,
  isStreaming,
  onPromptSubmit,
}: UsePromptTokenSubmissionArgs) {
  const consumedClassicTripMigrationTokenRef = useRef<string | null>(null)
  const consumedStagedChatPromptTokenRef = useRef<string | null>(null)
  const consumedHotelSearchChatPromptRef = useRef<string | null>(null)

  const classicTripMigrationToken = useMemo(() => {
    return new URLSearchParams(search).get(CLASSIC_TRIP_MIGRATION_QUERY_PARAM)
  }, [search])

  const stagedChatPromptToken = useMemo(() => {
    return new URLSearchParams(search).get(STAGED_CHAT_PROMPT_QUERY_PARAM)
  }, [search])

  const hotelSearchChatPrompt = useMemo(() => {
    return new URLSearchParams(search).get(HOTEL_SEARCH_CHAT_PROMPT_QUERY_PARAM)?.trim() || undefined
  }, [search])

  useEffect(() => {
    if (!isNewChatRoute || chatId !== undefined || isLoading || isStreaming) {
      return
    }

    if (
      classicTripMigrationToken &&
      consumedClassicTripMigrationTokenRef.current !== classicTripMigrationToken
    ) {
      const migrationPrompt = consumeClassicTripMigrationPrompt(classicTripMigrationToken)

      if (migrationPrompt) {
        consumedClassicTripMigrationTokenRef.current = classicTripMigrationToken
        onPromptSubmit(migrationPrompt)
        return
      }
    }

    if (
      stagedChatPromptToken &&
      consumedStagedChatPromptTokenRef.current !== stagedChatPromptToken
    ) {
      consumedStagedChatPromptTokenRef.current = stagedChatPromptToken
      const stagedPrompt = consumeStagedChatPrompt(stagedChatPromptToken)

      if (stagedPrompt) {
        onPromptSubmit(stagedPrompt)
        return
      }
    }

    if (hotelSearchChatPrompt && consumedHotelSearchChatPromptRef.current !== hotelSearchChatPrompt) {
      consumedHotelSearchChatPromptRef.current = hotelSearchChatPrompt
      onPromptSubmit(hotelSearchChatPrompt)
    }
  }, [
    classicTripMigrationToken,
    chatId,
    hotelSearchChatPrompt,
    isLoading,
    isNewChatRoute,
    isStreaming,
    onPromptSubmit,
    stagedChatPromptToken,
  ])
}
